import React, { useCallback, useEffect, useState } from 'react';
import { courseSupportingDataService } from '../../../../services/courseSupportingDataService';
import { CefrLevelForm, CefrLevel, CefrRegistry } from './CefrLevelForm';

const EMPTY_LEVEL: CefrLevel = {
  name: '',
  guidance: '',
};

export function CefrRegistryEditor() {
  const [registry, setRegistry] = useState<CefrRegistry | null>(null);
  const [originalJson, setOriginalJson] = useState('');
  const [etag, setEtag] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await courseSupportingDataService.get('cefr_levels');
        const parsed = JSON.parse(data.content) as CefrRegistry;
        setRegistry(parsed);
        setOriginalJson(JSON.stringify(parsed));
        setEtag(data.etag);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load CEFR registry');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const isDirty = registry ? JSON.stringify(registry) !== originalJson : false;

  const handleSave = useCallback(async () => {
    if (!registry) return;
    setSaving(true);
    setError(null);
    setSavedMessage(null);
    try {
      const content = JSON.stringify(registry, null, 2);
      const result = await courseSupportingDataService.update('cefr_levels', content, etag);
      setEtag(result.etag);
      setOriginalJson(JSON.stringify(registry));
      setSavedMessage('Saved successfully');
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [registry, etag]);

  const handleDiscard = () => {
    if (originalJson) {
      setRegistry(JSON.parse(originalJson));
      setExpandedId(null);
    }
    setError(null);
    setSavedMessage(null);
  };

  const updateLevel = (levelId: string, updated: CefrLevel) => {
    if (!registry) return;
    setRegistry({ ...registry, levels: { ...registry.levels, [levelId]: updated } });
  };

  const deleteLevel = (levelId: string) => {
    if (!registry) return;
    const next = { ...registry.levels };
    delete next[levelId];
    setRegistry({ ...registry, levels: next });
    setExpandedId(null);
  };

  const addLevel = () => {
    if (!registry) return;
    const newId = prompt('Enter level code (e.g., A1, B2):');
    if (!newId || !newId.trim()) return;
    const id = newId.trim().toUpperCase();
    if (registry.levels[id]) {
      setError(`Level ${id} already exists`);
      return;
    }
    setRegistry({
      ...registry,
      levels: { ...registry.levels, [id]: { ...EMPTY_LEVEL, name: `${id} — New Level` } },
    });
    setExpandedId(id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500" />
      </div>
    );
  }

  if (!registry) {
    return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">{error || 'Failed to load'}</div>;
  }

  const sortedLevels = Object.entries(registry.levels).sort(([a], [b]) => {
    const order = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'];
    return order.indexOf(a) - order.indexOf(b);
  });

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">CEFR Levels Registry</h2>
        <p className="text-sm text-gray-500 mt-1">
          Each level defines the vocabulary, grammar, discourse, and exercise rules the LLM must follow when generating content for that level. When you select a CEFR level in the course targeting, the matching guidance block is automatically pulled into every prompt in the pipeline.
        </p>
      </div>

      {/* Level list */}
      <div className="space-y-3">
        {sortedLevels.map(([levelId, level]) => {
          const isExpanded = expandedId === levelId;

          if (isExpanded) {
            return (
              <CefrLevelForm
                key={levelId}
                levelId={levelId}
                level={level}
                onChange={(updated) => updateLevel(levelId, updated)}
                onDelete={() => deleteLevel(levelId)}
                isNew={false}
              />
            );
          }

          return (
            <div
              key={levelId}
              className="bg-white border rounded p-3 cursor-pointer hover:bg-gray-50 transition-all"
              style={{ boxShadow: 'inset 0 0 0 0 #01bcd4' }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'inset 4px 0 0 0 #01bcd4'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'inset 0 0 0 0 #01bcd4'; }}
              onClick={() => setExpandedId(levelId)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 text-sm">{levelId}</span>
                    <span className="text-xs text-gray-500">{level.name}</span>
                  </div>
                  <div className="text-xs text-gray-400 mt-0.5 truncate max-w-2xl">{level.guidance.slice(0, 120)}...</div>
                </div>
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add button */}
      <button
        onClick={addLevel}
        className="w-full py-2 text-sm font-medium text-gray-600 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:text-blue-600 transition-colors"
      >
        + Add Level
      </button>

      {/* Actions */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={!isDirty || saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={handleDiscard}
          disabled={!isDirty}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Discard changes
        </button>
        {savedMessage && <span className="text-sm text-green-600">{savedMessage}</span>}
        {error && <span className="text-sm text-red-600">{error}</span>}
      </div>

      {/* Warning */}
      <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
        <svg className="w-4 h-4 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <span>Changes affect all future course generation. Level definitions propagate to every downstream stage.</span>
      </div>
    </div>
  );
}
